import type {UseMessagesReturn} from '@/components/ChatSection/hooks/useMessages'
import MinimalProductCard from '@/components/ProductCard/MinimalProductCard'
import Slider from '@/components/Slider/Slider'
import {useLocale} from '@/lib/locale/locale'
import {type Product as IProduct, type RawMinimalProduct} from '@/models/productModel'
import queries from '@/queries/queries'

import style from './SuggestedProducts.module.scss'

interface Props {
  rawProducts: RawMinimalProduct[]
  submitMessage: UseMessagesReturn['submitMessage']
}

export default memo(
  function SuggestedProducts({rawProducts, submitMessage}: Props) {
    const [locale] = useLocale()

    const products = useQueries({
      queries: rawProducts.map(rawProduct =>
        queries.getQueryOptions({
          name: 'product',
          params: [rawProduct.retailer, rawProduct.id, locale],
          enabled: false,
          queryFn: () => false,
        }),
      ),
    })
      .map(queries => queries.data)
      .filter(Boolean)

    return (
      // data-raw-products is using for the copy feature
      <div
        data-is-products
        data-raw-products={productsToCsv(products)}
        role='list'
        aria-label='Product list'
      >
        {rawProducts.length > 0 && (
          <Slider wrapperProps={{className: style.SuggestedProducts}}>
            {rawProducts.map((product, i) => {
              return (
                <div role='listitem' key={i}>
                  <MinimalProductCard rawMinimalProduct={product} submitMessage={submitMessage} />
                </div>
              )
            })}
          </Slider>
        )}
      </div>
    )
  },
  (prev, next) => prev.rawProducts.length === next.rawProducts.length,
)

const HEADER = '```csv\nretailer,url,image,title,price,rating,rating_count\n'
const FOOTER = '\n```'

function productsToCsv(products: IProduct[]) {
  return (
    HEADER +
    products
      .map(p => {
        return `"${p.retailer}","${p.url}","${p.image}","${p.title}","${p.price}","${p.rating}","${p.ratingCount}"`
      })
      .join('\n') +
    FOOTER
  )
}
